import React, { useState } from "react";
import { EXPERIENCE } from "../../data";
import type { ExperienceItem } from "../../types";
import { SectionHeader } from "../ui/SectionHeader";

// ── Sub-components ────────────────────────────────────────────────────────────

interface TabButtonProps {
  item: ExperienceItem;
  isActive: boolean;
  onClick: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({ item, isActive, onClick }) => (
  <button
    className={`exp-btn${isActive ? " on" : ""}`}
    onClick={onClick}
    aria-selected={isActive}
    role="tab"
  >
    <span className="exp-btn-co">{item.company}</span>
    <span className="exp-btn-period">{item.period}</span>
  </button>
);

interface ExperiencePaneProps {
  item: ExperienceItem;
  isActive: boolean;
}

const ExperiencePane: React.FC<ExperiencePaneProps> = ({ item, isActive }) => (
  <div
    className={`exp-pane${isActive ? " on" : ""}`}
    role="tabpanel"
    aria-hidden={!isActive}
  >
    <div className="ep-title">{item.role}</div>
    <div className="ep-co">{item.at}</div>
    <div className="ep-loc">{item.loc}</div>

    <ul className="ep-list">
      {item.bullets.map((bullet, i) => (
        <li key={i}>{bullet}</li>
      ))}
    </ul>

    <div className="ep-stack">
      {item.stack.map((tech) => (
        <span key={tech} className="ep-tag">
          {tech}
        </span>
      ))}
    </div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

export const Experience: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section id="experience">
      <div className="section-wrap">
        <SectionHeader
          label="Experience"
          title={
            <>
              Where I've <em>worked.</em>
            </>
          }
        />

        <div className="exp-layout" role="tablist">
          {/* Sidebar — tab buttons */}
          <div className="exp-nav">
            {EXPERIENCE.map((item, i) => (
              <TabButton
                key={item.company + item.period}
                item={item}
                isActive={activeIndex === i}
                onClick={() => setActiveIndex(i)}
              />
            ))}
          </div>

          {/* Content — tab panels */}
          <div>
            {EXPERIENCE.map((item, i) => (
              <ExperiencePane
                key={item.company + item.period}
                item={item}
                isActive={activeIndex === i}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
